import React, { useState, useEffect, useRef } from 'react';
import { Button, Dropdown, Input, Spin, Typography, Select } from 'antd';
import { FileTextOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import {
  getActiveTemplatesRequest,
  getTemplatesByCategoryRequest,
  searchTemplatesRequest,
} from 'providers/MessageTemplateProvider/slice';
import './styles.less';

const { Text } = Typography;

const TemplateSelector = ({ onSelectTemplate, disabled = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [inputValue, setInputValue] = useState(''); // What user types (immediate)
  const [searchValue, setSearchValue] = useState(''); // What triggers search (debounced)
  const [selectedCategory, setSelectedCategory] = useState(null);

  const loading = useSelector((state) => state.messageTemplate.loading);
  const searchLoading = useSelector((state) => state.messageTemplate.searchLoading);
  const activeTemplates = useSelector((state) => state.messageTemplate.activeTemplates);
  const searchResults = useSelector((state) => state.messageTemplate.searchResults);
  const categoryTemplates = useSelector((state) => state.messageTemplate.categoryTemplates);

  // Only fetch active templates when dropdown opens and no filters are active
  useEffect(() => {
    if (visible && !searchValue && !selectedCategory) {
      dispatch(getActiveTemplatesRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, visible]);

  // Create debounced search function using ref to avoid recreation
  const debouncedSearchRef = useRef(
    debounce((searchDispatch, value, setSearchValueFn) => {
      setSearchValueFn(value); // Update searchValue after debounce
      if (value.trim()) {
        searchDispatch(searchTemplatesRequest({ query: value }));
      } else {
        searchDispatch(getActiveTemplatesRequest());
      }
    }, 500)
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    const debouncedFn = debouncedSearchRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const handleSearchChange = (value) => {
    setInputValue(value); // Update input immediately for smooth typing
    if (selectedCategory) {
      setSelectedCategory(null);
    }
    debouncedSearchRef.current(dispatch, value, setSearchValue);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setInputValue('');
    setSearchValue('');
    dispatch(getTemplatesByCategoryRequest({ category }));
  };

  const handleTemplateSelect = (template) => {
    setVisible(false);
    onSelectTemplate(template);
  };

  const handleManageTemplates = () => {
    setVisible(false);
    navigate('/fonehouse/message-templates');
  };

  const renderPreviewContent = (content) => {
    if (!content) return '';

    // Replace placeholders with example values for preview
    const placeholders = {
      '{customer_name}': 'Khách hàng',
      '{product_name}': 'Sản phẩm',
      '{order_id}': 'Đơn hàng',
      '{price}': 'Giá',
      '{date}': 'Ngày',
    };

    let preview = content;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return preview;
  };

  const getDisplayTemplates = () => {
    if (searchValue) {
      return searchResults; // Show search results even if empty
    }
    if (selectedCategory) {
      return categoryTemplates; // Show category results even if empty
    }
    return activeTemplates;
  };

  const templates = getDisplayTemplates();

  const categoryOptions = [
    { value: null, label: 'Tất cả' },
    { value: 'greeting', label: 'Chào hỏi' },
    { value: 'sales', label: 'Bán hàng' },
    { value: 'location', label: 'Địa điểm' },
    { value: 'complaint', label: 'Khiếu nại' },
    { value: 'order', label: 'Đơn hàng' },
    { value: 'support', label: 'Hỗ trợ' },
    { value: 'promotion', label: 'Khuyến mãi' },
  ];

  const getCategoryColor = (category) => {
    const colors = {
      greeting: '#1890ff',
      sales: '#52c41a',
      location: '#fa8c16',
      complaint: '#eb2f96',
      order: '#722ed1',
      support: '#13c2c2',
      promotion: '#f5222d',
    };
    return colors[category] || '#1890ff';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      greeting: 'Chào hỏi',
      sales: 'Bán hàng',
      location: 'Địa điểm',
      complaint: 'Khiếu nại',
      order: 'Đơn hàng',
      support: 'Hỗ trợ',
      promotion: 'Khuyến mãi',
    };
    return labels[category] || category;
  };

  const handleCategoryChange = (value) => {
    if (value === null) {
      setSelectedCategory(null);
      setInputValue('');
      setSearchValue('');
      dispatch(getActiveTemplatesRequest());
    } else {
      handleCategoryFilter(value);
    }
  };

  const menu = (
    <div className="template-selector-dropdown">
      <div className="template-selector-header">
        <div className="header-row">
          <Input
            placeholder="Tìm kiếm template"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
          <Select
            value={selectedCategory}
            onChange={handleCategoryChange}
            options={categoryOptions}
            className="category-select"
            popupMatchSelectWidth={false}
          />
          <Button icon={<SettingOutlined />} onClick={handleManageTemplates} className="settings-button" />
        </div>
      </div>

      <div className="template-list">
        {loading || searchLoading ? (
          <div className="template-loading">
            <Spin size="small" />
            <Text type="secondary">Đang tải template...</Text>
          </div>
        ) : templates.length > 0 ? (
          templates.map((template) => (
            <div key={template._id} className="template-item" onClick={() => handleTemplateSelect(template)}>
              <div className="template-image-placeholder">
                {template.image ? (
                  <img src={typeof template.image === 'string' ? template.image : template.image.url} alt="Template" />
                ) : (
                  <span className="placeholder-text">img</span>
                )}
              </div>
              <div className="template-content">
                <div className="template-header">
                  <Text strong className="template-title">
                    {template.title}
                  </Text>
                </div>
                {(template.category || selectedCategory) && (
                  <div className="template-category">
                    <div className="category-indicator">
                      <span
                        className="category-square"
                        style={{ backgroundColor: getCategoryColor(template.category || selectedCategory) }}
                      />
                      <span style={{ color: getCategoryColor(template.category || selectedCategory) }}>
                        {getCategoryLabel(template.category || selectedCategory)}
                      </span>
                    </div>
                  </div>
                )}
                <Text type="secondary" className="template-preview">
                  {renderPreviewContent(template.content)}
                </Text>
              </div>
            </div>
          ))
        ) : (
          <div className="template-empty">
            <Text type="secondary">{inputValue ? 'Không tìm thấy template nào' : 'Chưa có template nào'}</Text>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={visible}
      onVisibleChange={(v) => !disabled && setVisible(v)}
      placement="topLeft"
      overlayClassName="template-selector-overlay"
      disabled={disabled}
    >
      <Button
        icon={<FileTextOutlined />}
        title="Chọn template tin nhắn"
        className="template-selector-button"
        size="small"
        type="text"
        disabled={disabled}
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      />
    </Dropdown>
  );
};

export default TemplateSelector;
